import { useState, useRef, useEffect } from "react";
import { Palette, X } from "lucide-react";

/**
 * Palette of brand-grade hex colors. Pick one to identify a firm visually.
 * The chosen color propagates to all of that firm's products (border + dot).
 */
export const FIRM_PALETTE = [
    "#F59E0B", "#3B82F6", "#10B981", "#EC4899", "#A855F7",
    "#06B6D4", "#F97316", "#84CC16", "#EF4444", "#8B5CF6",
    "#14B8A6", "#FBBF24", "#71717A",
];

export const NO_COLOR = "";

/**
 * Small inline button that opens a popover with the firm color palette.
 * @param {string} value — current hex (or "")
 * @param {(hex: string) => void} onChange
 * @param {string} label
 */
export default function ColorPicker({ value, onChange, label = "Brand color", testid = "color-picker" }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        if (open) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    return (
        <div className="relative inline-block" ref={ref}>
            <button
                onClick={() => setOpen((x) => !x)}
                data-testid={testid}
                title={label}
                className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 text-[10px] font-mono uppercase tracking-wider text-zinc-400 px-2 py-0.5 transition-colors"
            >
                {value ? (
                    <span className="w-2.5 h-2.5 inline-block rounded-full" style={{ background: value }} />
                ) : (
                    <Palette className="w-3 h-3" />
                )}
                {value ? value.toUpperCase() : "Color"}
            </button>

            {open && (
                <div data-testid={`${testid}-popover`} className="absolute left-0 top-full mt-1 z-40 bg-zinc-950 border border-zinc-800 p-2 shadow-xl">
                    <div className="flex items-center justify-between mb-2 px-1">
                        <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
                        <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-100"><X className="w-3 h-3" /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 w-44">
                        {FIRM_PALETTE.map((c) => (
                            <button
                                key={c}
                                onClick={() => { onChange(c); setOpen(false); }}
                                data-testid={`${testid}-swatch-${c.slice(1)}`}
                                title={c}
                                className={`w-5 h-5 border ${value === c ? "border-zinc-50" : "border-zinc-800 hover:border-zinc-500"}`}
                                style={{ background: c }}
                            />
                        ))}
                        <button
                            onClick={() => { onChange(NO_COLOR); setOpen(false); }}
                            data-testid={`${testid}-clear`}
                            title="Clear color"
                            className={`w-5 h-5 border border-zinc-800 hover:border-zinc-500 flex items-center justify-center ${!value ? "border-zinc-50" : ""}`}
                        >
                            <X className="w-2.5 h-2.5 text-zinc-500" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
